import type { ComponentPropsWithoutRef, ReactElement } from 'react'
import { controlClassName, isAriaInvalid } from './control-classes.ts'

/**
 * A native `<select>`, not a custom listbox.
 *
 * This is a deliberate refusal. A hand-rolled dropdown has to reimplement
 * typeahead, Home/End, PageUp/PageDown, the mobile wheel and the screen
 * reader's option-count announcement, and the versions that ship in admin
 * consoles almost never do. The native control gets all of it, and
 * `color-scheme` in `styles.css` is what makes its popup follow the dark
 * theme — which is the one thing that used to give a native select away.
 *
 * Options are `children` so the caller keeps `<optgroup>` and the empty
 * "— choose —" placeholder.
 */

export type SelectProps = Omit<ComponentPropsWithoutRef<'select'>, 'className'>

export function Select({ children, ...rest }: SelectProps): ReactElement {
  return (
    <select
      // Same 32px as a table row — a select in a toolbar lines up with
      // the rows below it. `pr-7` leaves room for the native arrow.
      className={`h-row pr-7 ${controlClassName({
        isInvalid: isAriaInvalid(rest['aria-invalid']),
      })}`}
      {...rest}
    >
      {children}
    </select>
  )
}
